import React, { useState, useEffect } from 'react';

interface Priority {
  ShortTitle: string;
  Description: string;
}

interface SimpleSliderProps {
  priorities?: Priority[];
}

const SimpleSlider: React.FC<SimpleSliderProps> = ({ priorities = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];
  

  // Minimum distance between touchStart and touchEnd to be considered a swipe
  const minSwipeDistance = 50;

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const nextSlide = () => {
    const next = currentIndex === priorities.length - 1 ? 0 : currentIndex + 1;
    goToSlide(next);
  };

  const prevSlide = () => {
    const prev = currentIndex === 0 ? priorities.length - 1 : currentIndex - 1;
    goToSlide(prev);
  };

  // Touch event handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && priorities.length > 1) {
      nextSlide();
    }
    if (isRightSwipe && priorities.length > 1) {
      prevSlide();
    }
  };

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (priorities.length <= 1) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, priorities.length]);

  if (priorities.length === 0) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '2rem',
        margin: '0 0.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        textAlign: 'center',
        color: '#6b7280'
      }}>
        
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Immediate Priorities</h3>
        <p style={{ margin: 0 }}>No immediate priorities available</p>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(90deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
      borderRadius: '20px',
      padding: '1.5rem 0',
      margin: '0 0.5rem',
      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.3), 0 4px 16px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      position: 'relative',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '0 1.5rem',
        gap: '0.75rem'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#ff4757',
          animation: 'pulse 2s infinite',
          boxShadow: '0 0 0 0 rgba(255, 71, 87, 0.7)'
        }}></div>
        <h3 style={{
          margin: 0,
          fontSize: '1.3rem',
          fontWeight: '700',
          color: 'white',
          textAlign: 'center',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          letterSpacing: '0.5px'
        }}>
          IMMEDIATE PRIORITIES
        </h3>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#ff4757',
          animation: 'pulse 2s infinite',
          boxShadow: '0 0 0 0 rgba(255, 71, 87, 0.7)'
        }}></div>
      </div>

      {/* Slider Container */}
      <div 
        style={{
          position: 'relative',
          height: '220px',
          overflow: 'visible',
          marginBottom: '1rem'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {priorities.map((priority, index) => {
          const color = colors[index % colors.length];
          
          const isActive = index === currentIndex;
          
          return (
            <div
              key={`priority-${index}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                padding: '0rem 1.5rem 0rem 1.5rem',                
                opacity: isActive ? 1 : 0,
                transform: `translateX(${isActive ? '0' : index > currentIndex ? '100%' : '-100%'})`,
                transition: 'all 0.3s ease-in-out',
                pointerEvents: isActive ? 'auto' : 'none'
                
              }}
            >
              <div style={{
                padding: '1.5rem',
                borderRadius: '20px',
                background: `linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.95) 0%, 
                  rgba(255, 255, 255, 0.9) 50%, 
                  rgba(255, 255, 255, 0.85) 100%
                )`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `2px solid rgba(255, 255, 255, 0.8)`,
                boxShadow: `
                  0 12px 40px rgba(0, 0, 0, 0.15),
                  0 4px 16px rgba(0, 0, 0, 0.1),
                  inset 0 1px 0 rgba(255, 255, 255, 1),
                  0 0 0 1px rgba(102, 126, 234, 0.2)
                `,
                position: 'relative',
                height: '160px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transform: isActive ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.3s ease-in-out'
              }}>
                

                {/* Urgency Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'linear-gradient(135deg, #ff4757, #ff3742)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 2px 8px rgba(255, 71, 87, 0.4)',
                  animation: 'pulse 2s infinite'
                }}>
                  URGENT
                </div>

                {/* Title */}
                <div style={{
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '0.75rem',
                  fontSize: '1.3rem',
                  lineHeight: '1.3',
                  paddingRight: '4rem',
                  letterSpacing: '-0.02em'
                }}>
                  {priority.ShortTitle}
                </div>

                {/* Description */}
                <div style={{
                  color: 'rgba(0, 0, 0, 0.8)',
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  flex: 1,
                  overflow: 'hidden',
                  fontWeight: '500',
                  letterSpacing: '-0.01em'
                }}>
                  {priority.Description}
                </div>

                {/* Priority Number */}
                {/* <div style={{
                  position: 'absolute',
                  bottom: '1.25rem',
                  right: '1.25rem',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${color}, ${color}CC)`,
                  border: `2px solid white`,
                  boxShadow: `
                    0 4px 12px rgba(0, 0, 0, 0.15),
                    0 0 0 2px ${color}40
                  `,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: 'white'
                }}>
                  {index + 1}
                </div> */}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      {/* {priorities.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            disabled={isTransitioning}
            style={{
              position: 'absolute',
              left: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: '#374151',
              zIndex: 10,
              opacity: isTransitioning ? 0.5 : 1
            }}
          >
            ‹
          </button>
          
          <button
            onClick={nextSlide}
            disabled={isTransitioning}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: '#374151',
              zIndex: 10,
              opacity: isTransitioning ? 0.5 : 1
            }}
          >
            ›
          </button>
        </>
      )} */}

      {/* Dot Indicators */}
      {priorities.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '1rem 1.5rem 0.5rem 1.5rem'
        }}>
          {priorities.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                background: currentIndex === index ? '#ff4757' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: currentIndex === index ? 'scale(1.3)' : 'scale(1)',
                opacity: isTransitioning ? 0.5 : 1,
                padding: 0,
                minWidth: '12px',
                minHeight: '12px',
                boxShadow: currentIndex === index ? '0 0 12px rgba(255, 71, 87, 0.6)' : 'none'
              }}
            />
          ))}
        </div>
      )}

      {/* CSS Animation Keyframes */}
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 71, 87, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SimpleSlider;
